import { Money } from '../ui/Money';
import { DepartmentStaffDialog } from './DepartmentStaffDialog';
import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell } from 'recharts';
import { TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Minus, ChevronDown, ChevronUp, Users, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Camera } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const BOX_COLORS = [
  { bg: 'bg-gradient-to-br from-violet-50 to-purple-50', border: 'border-violet-200', text: 'text-violet-700', activeBg: 'ring-2 ring-violet-400' },
  { bg: 'bg-gradient-to-br from-sky-50 to-blue-50', border: 'border-sky-200', text: 'text-sky-700', activeBg: 'ring-2 ring-sky-400' },
  { bg: 'bg-gradient-to-br from-amber-50 to-orange-50', border: 'border-amber-200', text: 'text-amber-700', activeBg: 'ring-2 ring-amber-400' },
  { bg: 'bg-gradient-to-br from-emerald-50 to-green-50', border: 'border-emerald-200', text: 'text-emerald-700', activeBg: 'ring-2 ring-emerald-400' },
  { bg: 'bg-gradient-to-br from-rose-50 to-pink-50', border: 'border-rose-200', text: 'text-rose-700', activeBg: 'ring-2 ring-rose-400' },
  { bg: 'bg-gradient-to-br from-teal-50 to-cyan-50', border: 'border-teal-200', text: 'text-teal-700', activeBg: 'ring-2 ring-teal-400' },
];

const SalaryCostCard = ({ stats, onCreateSnapshot }) => {
  const [staffByMethod, setStaffByMethod] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('token');

  const handleBoxClick = async (method) => {
    if (expanded === method) {
      setExpanded(null);
      return;
    }
    if (!staffByMethod) {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/dashboard/payment-method-staff`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStaffByMethod(res.data);
      } catch { /* silent */ }
      finally { setLoading(false); }
    }
    setExpanded(method);
  };

  const methods = stats?.salary_by_payment_method ? Object.entries(stats.salary_by_payment_method) : [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Monthly Salary Cost by Payment Method</CardTitle>
        <Button onClick={onCreateSnapshot} size="sm" variant="outline" data-testid="create-snapshot-btn">
          <Camera className="h-4 w-4 mr-2" />
          Save Monthly Snapshot
        </Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {methods.map(([method, amount], idx) => {
              const colors = BOX_COLORS[idx % BOX_COLORS.length];
              const isActive = expanded === method;
              const staff = staffByMethod?.[method] || [];
              return (
                <div
                  key={method}
                  className={`${colors.bg} p-4 rounded-lg border ${colors.border} cursor-pointer transition-all hover:shadow-md ${isActive ? colors.activeBg : ''}`}
                  onClick={() => handleBoxClick(method)}
                  data-testid={`payment-box-${method.replace(/[^a-zA-Z0-9]/g, '-')}`}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-600 font-medium">{method}</p>
                    <div className="flex items-center gap-1">
                      {staffByMethod && <Badge className="text-[9px] px-1.5 py-0 bg-white/70 text-gray-600">{staff.length}</Badge>}
                      {isActive ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
                    </div>
                  </div>
                  <p className={`text-xl font-bold ${colors.text}`}><Money value={amount} /></p>
                </div>
              );
            })}
          </div>

          {/* Expanded employee list */}
          {expanded && (
            <div className="bg-white border rounded-xl p-4 shadow-sm animate-in slide-in-from-top-2" data-testid="payment-staff-list">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-gray-500" />
                  <h4 className="text-sm font-semibold text-gray-800">{expanded}</h4>
                  <Badge className="text-[10px] px-1.5 py-0 bg-gray-100 text-gray-600">
                    {staffByMethod?.[expanded]?.length || 0} employees
                  </Badge>
                </div>
                <button onClick={() => setExpanded(null)} className="p-1 rounded-full hover:bg-gray-100">
                  <X className="h-4 w-4 text-gray-400" />
                </button>
              </div>
              {loading ? (
                <p className="text-sm text-gray-400 text-center py-4">Loading...</p>
              ) : (
                <div className="max-h-[300px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-gray-50">
                      <tr className="border-b">
                        <th className="text-left py-2 px-3 text-gray-500 font-medium">Employee</th>
                        <th className="text-left py-2 px-3 text-gray-500 font-medium">Position</th>
                        <th className="text-left py-2 px-3 text-gray-500 font-medium">Department</th>
                        <th className="text-right py-2 px-3 text-gray-500 font-medium">Salary (AED)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(staffByMethod?.[expanded] || []).map(emp => (
                        <tr key={emp.employee_code} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="py-2 px-3">
                            <p className="font-medium text-gray-800">{emp.name}</p>
                            <p className="text-[10px] text-gray-400">{emp.employee_code}</p>
                          </td>
                          <td className="py-2 px-3 text-gray-600">{emp.position}</td>
                          <td className="py-2 px-3 text-gray-600">{emp.department}</td>
                          <td className="py-2 px-3 text-right font-medium text-gray-700">{emp.total_salary?.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg border-2 border-green-300 mt-4">
            <p className="text-sm text-gray-600 font-medium">Total Monthly Payroll</p>
            <p className="text-3xl font-bold text-green-600">
              <Money value={stats?.total_monthly_salary || 0} />
            </p>
            <p className="text-xs text-gray-500 mt-2">
              {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} - Active Employees Only
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const PayrollHistoryChart = ({ payrollHistory }) => {
  const chartData = [...(payrollHistory || [])].reverse().map(p => ({
    month: `${MONTH_NAMES[p.month - 1]} ${p.year}`,
    payroll: p.total_payroll,
    employees: p.active_employees
  }));

  // Add current month if not in snapshots
  const now = new Date();
  const curKey = `${MONTH_NAMES[now.getMonth()]} ${now.getFullYear()}`;
  if (chartData.length > 0 && !chartData.find(d => d.month === curKey)) {
    // Current month data will be fetched from /current and passed as last item
  }

  const COLORS = ['#10b981', '#059669', '#047857', '#065f46', '#34d399', '#6ee7b7', '#a7f3d0', '#d1fae5', '#10b981', '#059669', '#047857', '#065f46'];

  return (
    <Card data-testid="payroll-history-chart">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-green-600" />
          Payroll History (Last 12 Months)
        </CardTitle>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <p>No payroll snapshots found. Click "Save Monthly Snapshot" to start tracking.</p>
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={chartData} barSize={36}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={60} />
                <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: 12 }}
                  formatter={(value, name) => [
                    name === 'payroll' ? `AED ${value.toLocaleString()}` : value,
                    name === 'payroll' ? 'Total Payroll' : 'Active Employees'
                  ]}
                />
                <Bar dataKey="payroll" radius={[6, 6, 0, 0]} name="payroll">
                  {chartData.map((entry, idx) => (
                    <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-6 mt-2">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded"></div>
                <span className="text-xs text-gray-500">Monthly Payroll (AED)</span>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const PayrollComparisonWidget = () => {
  const now = new Date();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [drillDept, setDrillDept] = useState(null);

  const yearOptions = Array.from({ length: 5 }, (_, i) => now.getFullYear() - i);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/payroll/comparison?month=${month}&year=${year}`);
        setData(res.data);
      } catch (e) {
        console.error('Failed to load payroll comparison', e);
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [month, year]);

  const selector = (
    <div className="flex items-center gap-2" data-testid="payroll-comparison-selectors">
      <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
        <SelectTrigger className="w-32 h-8 text-xs" data-testid="payroll-comparison-month">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {MONTH_FULL.map((m, i) => (
            <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
        <SelectTrigger className="w-24 h-8 text-xs" data-testid="payroll-comparison-year">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {yearOptions.map((y) => (
            <SelectItem key={y} value={String(y)}>{y}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const header = (title) => (
    <CardHeader>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-blue-600" />
          {title}
        </CardTitle>
        {selector}
      </div>
    </CardHeader>
  );

  if (loading) {
    return (
      <Card data-testid="payroll-comparison">
        {header('Payroll Comparison')}
        <CardContent>
          <p className="text-sm text-gray-400 text-center py-8">Loading comparison…</p>
        </CardContent>
      </Card>
    );
  }
  if (!data) {
    return (
      <Card data-testid="payroll-comparison">
        {header('Payroll Comparison')}
        <CardContent>
          <p className="text-sm text-gray-400 text-center py-8">Could not load payroll comparison.</p>
        </CardContent>
      </Card>
    );
  }

  const { current_month, current_year, previous_year, current_total, previous_total, departments, has_current_data, current_is_live } = data;
  const monthName = MONTH_NAMES[current_month - 1];
  const totalChange = current_total - previous_total;
  const totalPct = previous_total > 0 ? ((totalChange / previous_total) * 100).toFixed(1) : 'N/A';

  const chartData = departments.map(d => ({
    name: d.name,
    current: d.current,
    previous: d.previous,
  }));

  return (
    <Card data-testid="payroll-comparison">
      {header(`Payroll Comparison: ${monthName} ${current_year} vs ${monthName} ${previous_year}`)}
      <CardContent className="space-y-6">
        {!has_current_data && !current_is_live && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700" data-testid="payroll-comparison-no-snapshot">
            No saved payroll snapshot for {MONTH_FULL[current_month - 1]} {current_year}. Showing AED 0 for that period — save a monthly snapshot to track it.
          </div>
        )}
        {/* Summary totals */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <p className="text-xs text-gray-500 mb-1">{monthName} {current_year}</p>
            <p className="text-2xl font-bold text-blue-700"><Money value={current_total} /></p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
            <p className="text-xs text-gray-500 mb-1">{monthName} {previous_year}</p>
            <p className="text-2xl font-bold text-gray-600"><Money value={previous_total} /></p>
          </div>
          <div className={`rounded-lg p-4 text-center border ${totalChange >= 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
            <p className="text-xs text-gray-500 mb-1">Difference</p>
            <p className={`text-2xl font-bold ${totalChange >= 0 ? 'text-red-600' : 'text-green-600'}`}>
              {totalChange >= 0 ? '+' : ''}{totalChange.toLocaleString()}
            </p>
            <p className={`text-xs ${totalChange >= 0 ? 'text-red-500' : 'text-green-500'}`}>
              ({totalPct}%)
            </p>
          </div>
        </div>

        {/* Bar chart */}
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} barGap={2}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
            <XAxis dataKey="name" tick={{ fontSize: 9 }} angle={-20} textAnchor="end" height={55} />
            <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} tick={{ fontSize: 11 }} />
            <Tooltip
              contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: 12 }}
              formatter={(value) => [`AED ${value.toLocaleString()}`]}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="current" name={`${monthName} ${current_year}`} fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={24} />
            <Bar dataKey="previous" name={`${monthName} ${previous_year}`} fill="#94a3b8" radius={[4, 4, 0, 0]} barSize={24} />
          </BarChart>
        </ResponsiveContainer>

        {/* Department table */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-3 text-gray-500 font-medium">Department</th>
                <th className="text-right py-2 px-3 text-gray-500 font-medium">{monthName} {current_year}</th>
                <th className="text-right py-2 px-3 text-gray-500 font-medium">{monthName} {previous_year}</th>
                <th className="text-right py-2 px-3 text-gray-500 font-medium">Change</th>
              </tr>
            </thead>
            <tbody>
              {departments.map(d => (
                <tr key={d.name} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-2 px-3 font-medium text-gray-700">
                    <button type="button" onClick={() => setDrillDept(d.name)} title="See staff in this department"
                      className="text-left text-violet-700 hover:text-violet-900 hover:underline underline-offset-2 transition-colors" data-testid={`payroll-dept-link-${d.name}`}>{d.name}</button>
                  </td>
                  <td className="py-2 px-3 text-right text-blue-700 font-medium"><Money value={d.current} /></td>
                  <td className="py-2 px-3 text-right text-gray-500"><Money value={d.previous} /></td>
                  <td className="py-2 px-3 text-right">
                    <span className={`inline-flex items-center gap-0.5 font-medium ${d.change > 0 ? 'text-red-600' : d.change < 0 ? 'text-green-600' : 'text-gray-400'}`}>
                      {d.change > 0 ? <ArrowUpRight className="h-3 w-3" /> : d.change < 0 ? <ArrowDownRight className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
                      <Money value={Math.abs(d.change)} />
                    </span>
                  </td>
                </tr>
              ))}
              {/* Total row */}
              <tr className="border-t-2 border-gray-300 bg-gray-50 font-bold">
                <td className="py-2 px-3 text-gray-800">TOTAL</td>
                <td className="py-2 px-3 text-right text-blue-800"><Money value={current_total} /></td>
                <td className="py-2 px-3 text-right text-gray-600"><Money value={previous_total} /></td>
                <td className="py-2 px-3 text-right">
                  <span className={`inline-flex items-center gap-0.5 ${totalChange > 0 ? 'text-red-600' : totalChange < 0 ? 'text-green-600' : 'text-gray-400'}`}>
                    {totalChange > 0 ? <ArrowUpRight className="h-3 w-3" /> : totalChange < 0 ? <ArrowDownRight className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
                    <Money value={Math.abs(totalChange)} />
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <DepartmentStaffDialog department={drillDept} month={month} year={year} onClose={() => setDrillDept(null)} />
      </CardContent>
    </Card>
  );
};

export { SalaryCostCard, PayrollHistoryChart, PayrollComparisonWidget };
